/**
 * Detailed Regulator Simulation - Protocol B
 *
 * Simulates comprehensive communication between HOST and a single REGULATOR
 * using most non-IP Protocol B commands.
 *
 * Commands implemented:
 * - 0x81: Identificación del regulador
 * - 0x82: Estado del regulador
 * - 0x83: Consulta de alarmas
 * - 0x84: Selección de plan
 * - 0x85: Plan en curso
 * - 0x86: Posición en ciclo
 * - 0x87: Estado de grupos semafóricos
 * - 0xBF 0x46: Consulta/Programación de hora
 * - 0xBF 0x47: Consulta de versión
 * - 0xC4: Detectores tiempo real
 * - 0xC5: Notificación cambios de estado
 * - 0xC6: Demandas peatonales
 * - 0xD0: Petición de plan completo
 * - 0xD4 0x45: Test de equipos
 * - 0xD6: Alarmas tiempo real
 * - 0xD7: Histórico de alarmas
 */

import { create } from 'zustand';
import { Frame, FrameType, ControlCodes } from '../types/protocol';
import { useFrameStore } from './frameStore';
import { buildDataFrame, createFrame } from '../utils/frameBuilder';

// ============================================================================
// TYPES AND CONSTANTS
// ============================================================================

// Regulator states (estado del regulador)
export const RegulatorStates = {
  0: 'Apagado',
  1: 'Intermitente',
  2: 'Todo Rojo',
  3: 'Manual Local',
  4: 'Automático Local',
  5: 'Demanda',
  6: 'Centralizado',
  7: 'Forzado Central',
  8: 'Test Lámparas',
} as const;

// State change origins
export const StateOrigins = {
  'A': 'Automático',
  'E': 'Externo (Central)',
  'L': 'Local (Panel)',
  'M': 'Manual',
  'P': 'Programador',
  'R': 'Reset',
  'T': 'Timeout',
} as const;

// Alarm codes — catálogo del Protocolo B §3.2.2.4.3. Subconjunto de alta
// confianza usado para traducir el código en vivo (AlarmPanel/AlarmOverlay).
// El catálogo completo (incluye códigos de confianza media/baja y sin
// documentar, para consulta) vive en `src/data/alarmCatalog.ts` — no se usa
// aquí a propósito: un operador viendo una alarma real no debe ver una
// descripción especulativa.
export const AlarmCodes = {
  '@': 'Fallo test equipos',
  'A': 'Alarma tensión',
  'B': 'Alarma batería',
  'C': 'Incompatibilidad color',
  'D': 'Alarma demanda',
  'E': 'Error sistema',
  'F': 'Timeout fase',
  'G': 'Sustitución grupo',
  'H': 'Tiempo excedido',
  'I': 'Incompatibilidad',
  'J': 'Lámpara fundida',
  'K': 'Manipulación local',
  'L': 'Potencia fundida',
  'M': 'Modo manual',
  'N': 'Temperatura',
  'R': 'Reset',
  'S': 'Sincronización',
  'T': 'Timeout comunicación',
  'V': 'Rango tensión',
  'X': 'Alarma externa',
  'KP': 'Puerta del armario abierta',
  'KG': 'Guardia / paso a intermitente forzado',
  'KM': 'Modo manual (regulador a OFF)',
  'KR': 'Secuencia de todo rojo (incompatibilidad)',
  'KS': 'Pérdida de señal de sincronismo',
  'KU': 'Llamada de emergencia activada',
  'TC': 'Pérdida de comunicación con central',
  'TO': 'Timeout de identificación de sistema',
  'Ls': 'Lámpara fundida (potencia)',
  'AAA': 'Tensión de alimentación fuera de rango',
  'AAS': 'Tensión de las salidas fuera de rango',
} as const;

// Command codes
export const Commands = {
  // Information commands (0x8x)
  IDENTIFICATION: 0x81,
  REGULATOR_STATE: 0x82,
  ALARM_QUERY: 0x83,
  PLAN_SELECT: 0x84,
  CURRENT_PLAN: 0x85,
  CYCLE_POSITION: 0x86,
  GROUP_STATUS: 0x87,

  // Extended commands (0xBF + subcode)
  TIME_QUERY: 0x46,      // 0xBF 0x46
  VERSION_QUERY: 0x47,   // 0xBF 0x47

  // Notification commands (0xCx)
  DETECTOR_RT: 0xC4,
  STATE_CHANGE: 0xC5,
  PEDESTRIAN_DEMAND: 0xC6,

  // Data commands (0xDx)
  FULL_PLAN: 0xD0,
  EQUIPMENT_TEST: 0xD4,  // 0xD4 0x45
  ALARM_RT: 0xD6,
  ALARM_HISTORY: 0xD7,
} as const;

// Simulation event types
type SimulationEvent = {
  type: 'tx' | 'rx';
  delay: number; // ms from previous event
  generator: () => Uint8Array;
  frameType: FrameType;
  description: string;
};

// ============================================================================
// FRAME BUILDERS
// ============================================================================

// ============================================================================
// COMMAND GENERATORS - HOST → REGULATOR (TX)
// ============================================================================

let messageCounter = 1;

function getCounter(): number {
  return (messageCounter++ % 256);
}

// Standard header for HOST → REGULATOR
function hostHeader(expectsResponse: boolean = true): number[] {
  return [
    expectsResponse ? 0x21 : 0x01,  // Channel dest=1, expects response flag
    0x21,                            // Channel orig=1
    0x01, 0x01,                      // Destination: H0-C1-R1-S0
    0x00, 0x00,                      // Origin: H0-C0-R0-S0 (HOST)
    getCounter()                     // Message counter
  ];
}

// Response header for REGULATOR → HOST
function regulatorHeader(counter: number): number[] {
  return [
    0x81,       // Response flag + channel
    0x01,       // Channel
    0x00, 0x00, // Destination: HOST
    0x01, 0x01, // Origin: Regulator
    counter     // Same counter as request
  ];
}

// --- TX Commands (HOST → REGULATOR) ---

// 0x81 - Identification query
function txIdentification(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.IDENTIFICATION]));
}

// 0x82 - Regulator state query
function txRegulatorState(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.REGULATOR_STATE]));
}

// 0x83 - Alarm query
function txAlarmQuery(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.ALARM_QUERY]));
}

// 0x84 - Plan selection
function txPlanSelect(state: number, plan: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...hostHeader(false), // No response expected for commands
    Commands.PLAN_SELECT,
    state,        // Target state (6 = centralized)
    plan,         // Plan number
    0xFF, 0xFF, 0xFF // Reference: -1 (use last)
  ]));
}

// 0x85 - Current plan query
function txCurrentPlan(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.CURRENT_PLAN]));
}

// 0x86 - Cycle position query
function txCyclePosition(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.CYCLE_POSITION]));
}

// 0x87 - Group status query
function txGroupStatus(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.GROUP_STATUS]));
}

// 0xBF 0x46 - Time query
function txTimeQuery(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), 0xBF, Commands.TIME_QUERY]));
}

// 0xBF 0x47 - Version query
function txVersionQuery(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), 0xBF, Commands.VERSION_QUERY]));
}

// 0xC4 - Real-time detector query
function txDetectorRT(detectorMask: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...hostHeader(),
    Commands.DETECTOR_RT,
    0x00,  // Type: 0 = physical detectors
    (detectorMask >> 24) & 0xFF,
    (detectorMask >> 16) & 0xFF,
    (detectorMask >> 8) & 0xFF,
    detectorMask & 0xFF
  ]));
}

// 0xC5 - State change notification request
function txStateChangeNotif(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.STATE_CHANGE]));
}

// 0xC6 - Pedestrian demand query
function txPedestrianDemand(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), Commands.PEDESTRIAN_DEMAND]));
}

// 0xD0 - Full plan query
function txFullPlan(planNumber: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...hostHeader(),
    Commands.FULL_PLAN,
    planNumber
  ]));
}

// 0xD4 0x45 - Equipment test
function txEquipmentTest(): Uint8Array {
  return buildDataFrame(new Uint8Array([...hostHeader(), 0xD4, 0x45]));
}

// 0xD6 - Real-time alarm notification setup
function txAlarmRT(): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...hostHeader(),
    Commands.ALARM_RT,
    0x01  // Enable notifications
  ]));
}

// 0xD7 - Alarm history query
function txAlarmHistory(): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...hostHeader(),
    Commands.ALARM_HISTORY,
    0x00, 0x00,  // Start index
    0x00, 0x10   // Count (16 entries)
  ]));
}

// ============================================================================
// RESPONSE GENERATORS - REGULATOR → HOST (RX)
// ============================================================================

// 0x81 - Identification response
function rxIdentification(counter: number): Uint8Array {
  // Identification data: model, version, serial, etc.
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    0x00,  // No error
    // Model: "REG-2000"
    0x52, 0x45, 0x47, 0x2D, 0x32, 0x30, 0x30, 0x30,
    // Version: 3.50
    0x03, 0x32,
    // Serial: 12345678
    0x00, 0xBC, 0x61, 0x4E,
    // Capabilities flags
    0x0F, 0xFF
  ]));
}

// Map UI phase label (F1/F2/F3/F4) to Protocol B single-byte ASCII code (A/B/C/D)
// The protocol serializes the phase as one byte; UI uses a more descriptive label.
function phaseToByte(phase: string): number {
  const map: Record<string, string> = { F1: 'A', F2: 'B', F3: 'C', F4: 'D' };
  const code = map[phase] ?? phase[0] ?? 'A';
  return code.charCodeAt(0);
}

// 0x82 - Regulator state response
function rxRegulatorState(counter: number, state: number, origin: string, phase: string): Uint8Array {
  const now = new Date();
  const julianDays = Math.floor((now.getTime() - new Date(1980, 0, 1).getTime()) / 86400000);
  const deciseconds = (now.getHours() * 36000) + (now.getMinutes() * 600) + (now.getSeconds() * 10);

  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    state,                    // Current state
    origin.charCodeAt(0),     // Origin code
    phaseToByte(phase),       // Current phase
    (julianDays >> 8) & 0xFF,
    julianDays & 0xFF,
    (deciseconds >> 16) & 0xFF,
    (deciseconds >> 8) & 0xFF,
    deciseconds & 0xFF
  ]));
}

// 0x83 - Alarm query response
function rxAlarmQuery(counter: number, alarms: string[]): Uint8Array {
  const alarmCodes = alarms.map(a => a.charCodeAt(0));
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    alarms.length,  // Number of active alarms
    ...alarmCodes   // Alarm codes
  ]));
}

// 0x85 - Current plan response
function rxCurrentPlan(counter: number, plan: number, cycleTime: number, phases: number[]): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    plan,           // Current plan number
    0x00, 0x01, 0x5F, 0x90,  // Reference time
    0x31, 0x18,     // Structure indices
    0x00, 0x00,     // Offset
    (cycleTime >> 8) & 0xFF,
    cycleTime & 0xFF,
    // Phase durations
    ...phases.flatMap(p => [(p >> 8) & 0xFF, p & 0xFF])
  ]));
}

// 0x86 - Cycle position response
function rxCyclePosition(counter: number, phase: string, position: number, cycleTime: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    phaseToByte(phase),
    (position >> 8) & 0xFF,
    position & 0xFF,
    (cycleTime >> 8) & 0xFF,
    cycleTime & 0xFF
  ]));
}

// 0x87 - Group status response (8 groups)
function rxGroupStatus(counter: number, groupStates: number[]): Uint8Array {
  // Each group: 2 bits (00=off, 01=red, 10=amber, 11=green)
  // Pack 4 groups per byte
  const packed: number[] = [];
  for (let i = 0; i < groupStates.length; i += 4) {
    let byte = 0;
    for (let j = 0; j < 4 && i + j < groupStates.length; j++) {
      byte |= (groupStates[i + j] & 0x03) << (6 - j * 2);
    }
    packed.push(byte);
  }

  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    groupStates.length,  // Number of groups
    ...packed            // Packed group states
  ]));
}

// 0xBF 0x46 - Time response
function rxTimeQuery(counter: number): Uint8Array {
  const now = new Date();
  const julianDays = Math.floor((now.getTime() - new Date(1980, 0, 1).getTime()) / 86400000);
  const deciseconds = (now.getHours() * 36000) + (now.getMinutes() * 600) + (now.getSeconds() * 10);

  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    (julianDays >> 8) & 0xFF,
    julianDays & 0xFF,
    (deciseconds >> 24) & 0xFF,
    (deciseconds >> 16) & 0xFF,
    (deciseconds >> 8) & 0xFF,
    deciseconds & 0xFF
  ]));
}

// 0xBF 0x47 - Version response
function rxVersionQuery(counter: number): Uint8Array {
  // Version string: "PROTO-B V3.50 BCN"
  const version = "PROTO-B V3.50 BCN";
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    ...Array.from(version).map(c => c.charCodeAt(0)),
    0x00  // Null terminator
  ]));
}

// 0xC4 - Detector response
function rxDetectorRT(counter: number, activeDetectors: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    0x00,  // No error
    (activeDetectors >> 24) & 0xFF,
    (activeDetectors >> 16) & 0xFF,
    (activeDetectors >> 8) & 0xFF,
    activeDetectors & 0xFF
  ]));
}

// 0xC5 - State change response
function rxStateChange(counter: number, state: number, origin: string): Uint8Array {
  const now = new Date();
  const julianDays = Math.floor((now.getTime() - new Date(1980, 0, 1).getTime()) / 86400000);
  const deciseconds = (now.getHours() * 36000) + (now.getMinutes() * 600) + (now.getSeconds() * 10);

  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    state,
    origin.charCodeAt(0),
    (julianDays >> 8) & 0xFF,
    julianDays & 0xFF,
    (deciseconds >> 16) & 0xFF,
    (deciseconds >> 8) & 0xFF,
    deciseconds & 0xFF
  ]));
}

// 0xC6 - Pedestrian demand response
function rxPedestrianDemand(counter: number, demands: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    (demands >> 8) & 0xFF,
    demands & 0xFF
  ]));
}

// 0xD0 - Full plan response (simplified)
function rxFullPlan(counter: number, planNumber: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    planNumber,
    // Simplified plan data
    0x04,        // 4 phases
    0x08,        // 8 groups
    // Phase A
    0x01, 0x2C,  // Duration 300 ds (30s)
    0xC0,        // Groups 1,2 green
    // Phase B (amber)
    0x00, 0x32,  // Duration 50 ds (5s)
    0x30,        // Groups 1,2 amber
    // Phase C
    0x01, 0x2C,  // Duration 300 ds
    0x0C,        // Groups 3,4 green
    // Phase D (amber)
    0x00, 0x32,  // Duration 50 ds
    0x03,        // Groups 3,4 amber
  ]));
}

// 0xD4 0x45 - Equipment test response
function rxEquipmentTest(counter: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    0x0F,  // All tests passed (bit mask)
    0x00,  // No errors
    0x08,  // 8 lamp groups tested
    0xFF,  // All lamps OK
    0x04,  // 4 detectors tested
    0x0F   // All detectors OK
  ]));
}

// 0xD6 - Alarm RT response
function rxAlarmRT(counter: number): Uint8Array {
  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    0x00  // Notifications enabled successfully
  ]));
}

// 0xD7 - Alarm history response
function rxAlarmHistory(counter: number): Uint8Array {
  const now = new Date();
  const julianDays = Math.floor((now.getTime() - new Date(1980, 0, 1).getTime()) / 86400000);

  return buildDataFrame(new Uint8Array([
    ...regulatorHeader(counter),
    0x03,  // 3 historical alarms
    // Alarm 1: Lamp burned (J) - yesterday
    0x4A,  // 'J'
    ((julianDays - 1) >> 8) & 0xFF,
    (julianDays - 1) & 0xFF,
    0x00, 0x8C, 0xA0,  // 10:00:00
    // Alarm 2: Voltage (A) - 2 days ago
    0x41,  // 'A'
    ((julianDays - 2) >> 8) & 0xFF,
    (julianDays - 2) & 0xFF,
    0x00, 0x57, 0xE4,  // 06:15:00
    // Alarm 3: Reset (R) - 3 days ago
    0x52,  // 'R'
    ((julianDays - 3) >> 8) & 0xFF,
    (julianDays - 3) & 0xFF,
    0x00, 0x00, 0x00   // 00:00:00
  ]));
}

// Flow control frames
function txTOK(): Uint8Array {
  return new Uint8Array([ControlCodes.TOK]);
}

function rxTRT(): Uint8Array {
  return new Uint8Array([ControlCodes.TRT]);
}

function txTML(): Uint8Array {
  return new Uint8Array([ControlCodes.TML]);
}

function rxACK(seq: number): Uint8Array {
  return new Uint8Array([ControlCodes.ESC, seq === 0 ? ControlCodes.ACK0 : ControlCodes.ACK1]);
}

// ============================================================================
// SIMULATION SEQUENCES
// ============================================================================

interface DetailedSimulationState {
  isRunning: boolean;
  speed: number;
  currentSequence: string;
  sequenceIndex: number;

  // Regulator state
  regulatorState: number;
  currentPhase: string;
  currentPlan: number;
  cyclePosition: number;
  cycleTime: number;
  phaseDurations: number[];
  planVersion: number;
  activeAlarms: string[];
  activeDetectors: number;
  pedestrianDemands: number;

  // Timing
  intervalId: NodeJS.Timeout | null;
  eventQueue: SimulationEvent[];

  // Actions
  start: () => void;
  stop: () => void;
  reset: () => void;
  setSpeed: (speed: number) => void;
  setPlan: (plan: number, cycleTime: number, phaseDurations: number[]) => void;
}

// Generate startup sequence
function generateStartupSequence(): SimulationEvent[] {
  let counter = 1;

  return [
    // Initial communication test
    { type: 'tx', delay: 0, generator: txTOK, frameType: FrameType.TEST_COMMUNICATION, description: 'TOK - Test comunicación' },
    { type: 'rx', delay: 50, generator: rxTRT, frameType: FrameType.TEST_COMMUNICATION, description: 'TRT - Respuesta test' },

    // Identification
    { type: 'tx', delay: 200, generator: txIdentification, frameType: FrameType.INFORMATION, description: '0x81 - Consulta identificación' },
    { type: 'rx', delay: 80, generator: () => rxIdentification(counter++), frameType: FrameType.INFORMATION, description: '0x81 - Respuesta identificación' },

    // Version query
    { type: 'tx', delay: 300, generator: txVersionQuery, frameType: FrameType.INFORMATION, description: '0xBF47 - Consulta versión' },
    { type: 'rx', delay: 60, generator: () => rxVersionQuery(counter++), frameType: FrameType.INFORMATION, description: '0xBF47 - Respuesta versión' },

    // Time synchronization
    { type: 'tx', delay: 200, generator: txTimeQuery, frameType: FrameType.INFORMATION, description: '0xBF46 - Consulta hora' },
    { type: 'rx', delay: 70, generator: () => rxTimeQuery(counter++), frameType: FrameType.INFORMATION, description: '0xBF46 - Respuesta hora' },

    // Equipment test
    { type: 'tx', delay: 500, generator: txEquipmentTest, frameType: FrameType.INFORMATION, description: '0xD445 - Test equipos' },
    { type: 'rx', delay: 150, generator: () => rxEquipmentTest(counter++), frameType: FrameType.INFORMATION, description: '0xD445 - Resultado test' },

    // Regulator state
    { type: 'tx', delay: 300, generator: txRegulatorState, frameType: FrameType.INFORMATION, description: '0x82 - Consulta estado' },
    { type: 'rx', delay: 60, generator: () => rxRegulatorState(counter++, 6, 'E', 'F1'), frameType: FrameType.INFORMATION, description: '0x82 - Estado: Centralizado' },

    // Alarm query
    { type: 'tx', delay: 200, generator: txAlarmQuery, frameType: FrameType.INFORMATION, description: '0x83 - Consulta alarmas' },
    { type: 'rx', delay: 50, generator: () => rxAlarmQuery(counter++, []), frameType: FrameType.INFORMATION, description: '0x83 - Sin alarmas activas' },

    // Alarm history
    { type: 'tx', delay: 300, generator: txAlarmHistory, frameType: FrameType.INFORMATION, description: '0xD7 - Histórico alarmas' },
    { type: 'rx', delay: 100, generator: () => rxAlarmHistory(counter++), frameType: FrameType.INFORMATION, description: '0xD7 - 3 alarmas históricas' },

    // Enable real-time alarms
    { type: 'tx', delay: 200, generator: txAlarmRT, frameType: FrameType.INFORMATION, description: '0xD6 - Activar alarmas RT' },
    { type: 'rx', delay: 40, generator: () => rxAlarmRT(counter++), frameType: FrameType.INFORMATION, description: '0xD6 - Alarmas RT activadas' },

    // Current plan
    { type: 'tx', delay: 300, generator: txCurrentPlan, frameType: FrameType.INFORMATION, description: '0x85 - Consulta plan actual' },
    { type: 'rx', delay: 80, generator: () => rxCurrentPlan(counter++, 2, 850, [360, 110, 230, 150]), frameType: FrameType.INFORMATION, description: '0x85 - Plan 2 activo' },

    // Full plan details
    { type: 'tx', delay: 400, generator: () => txFullPlan(2), frameType: FrameType.INFORMATION, description: '0xD0 - Petición plan completo' },
    { type: 'rx', delay: 120, generator: () => rxFullPlan(counter++, 2), frameType: FrameType.INFORMATION, description: '0xD0 - Datos plan 2' },

    // Group status
    { type: 'tx', delay: 300, generator: txGroupStatus, frameType: FrameType.INFORMATION, description: '0x87 - Estado grupos' },
    { type: 'rx', delay: 50, generator: () => rxGroupStatus(counter++, [3, 3, 1, 1, 1, 1, 1, 1]), frameType: FrameType.INFORMATION, description: '0x87 - G1,G2 verde, resto rojo' },

    // Cycle position
    { type: 'tx', delay: 200, generator: txCyclePosition, frameType: FrameType.INFORMATION, description: '0x86 - Posición ciclo' },
    { type: 'rx', delay: 40, generator: () => rxCyclePosition(counter++, 'F1', 150, 700), frameType: FrameType.INFORMATION, description: '0x86 - Fase F1, pos 150' },

    // Detector setup
    { type: 'tx', delay: 300, generator: () => txDetectorRT(0x0F), frameType: FrameType.INFORMATION, description: '0xC4 - Consulta detectores' },
    { type: 'rx', delay: 60, generator: () => rxDetectorRT(counter++, 0x05), frameType: FrameType.INFORMATION, description: '0xC4 - Det 1,3 activos' },

    // Pedestrian demands
    { type: 'tx', delay: 200, generator: txPedestrianDemand, frameType: FrameType.INFORMATION, description: '0xC6 - Demandas peatonales' },
    { type: 'rx', delay: 50, generator: () => rxPedestrianDemand(counter++, 0x00), frameType: FrameType.INFORMATION, description: '0xC6 - Sin demandas' },

    // Plan selection (change to plan 3, centralized)
    { type: 'tx', delay: 500, generator: () => txPlanSelect(6, 2), frameType: FrameType.INFORMATION, description: '0x84 - Selección plan 2' },
    { type: 'rx', delay: 30, generator: () => rxACK(0), frameType: FrameType.FLOW_CONTROL, description: 'ACK0 - Confirmación' },

    // State change notification
    { type: 'tx', delay: 300, generator: txStateChangeNotif, frameType: FrameType.INFORMATION, description: '0xC5 - Notif cambio estado' },
    { type: 'rx', delay: 70, generator: () => rxStateChange(counter++, 6, 'E'), frameType: FrameType.INFORMATION, description: '0xC5 - Estado centralizado' },
  ];
}

// Generate periodic polling sequence
function generatePollingSequence(phase: string, position: number): SimulationEvent[] {
  let counter = messageCounter;

  const events: SimulationEvent[] = [
    // Communication test
    { type: 'tx', delay: 0, generator: txTOK, frameType: FrameType.TEST_COMMUNICATION, description: 'TOK - Polling' },
    { type: 'rx', delay: 40, generator: rxTRT, frameType: FrameType.TEST_COMMUNICATION, description: 'TRT - Respuesta' },

    // Cycle position (most frequent query)
    { type: 'tx', delay: 100, generator: txCyclePosition, frameType: FrameType.INFORMATION, description: '0x86 - Posición ciclo' },
    { type: 'rx', delay: 50, generator: () => rxCyclePosition(counter++, phase, position, 850), frameType: FrameType.INFORMATION, description: `0x86 - Fase ${phase}, pos ${position}` },
  ];

  // Occasionally query detectors (30% chance)
  if (Math.random() < 0.3) {
    const activeDetectors = Math.floor(Math.random() * 16); // Random detector state
    events.push(
      { type: 'tx', delay: 150, generator: () => txDetectorRT(0x0F), frameType: FrameType.INFORMATION, description: '0xC4 - Consulta detectores' },
      { type: 'rx', delay: 50, generator: () => rxDetectorRT(counter++, activeDetectors), frameType: FrameType.INFORMATION, description: `0xC4 - Detectores: 0x${activeDetectors.toString(16).toUpperCase()}` }
    );
  }

  // Occasionally query pedestrian demands (20% chance)
  if (Math.random() < 0.2) {
    const demands = Math.random() < 0.3 ? (1 << Math.floor(Math.random() * 4)) : 0;
    events.push(
      { type: 'tx', delay: 100, generator: txPedestrianDemand, frameType: FrameType.INFORMATION, description: '0xC6 - Demandas peatonales' },
      { type: 'rx', delay: 40, generator: () => rxPedestrianDemand(counter++, demands), frameType: FrameType.INFORMATION, description: demands ? `0xC6 - Demanda activa` : '0xC6 - Sin demandas' }
    );
  }

  // Occasionally query group status (25% chance)
  if (Math.random() < 0.25) {
    const groupStates = phase === 'F1' || phase === 'F2'
      ? [3, 3, 1, 1, 1, 1, 1, 1]  // N-S green/amber
      : [1, 1, 3, 3, 1, 1, 1, 1]; // E-W green/amber

    if (phase === 'F2') groupStates[0] = groupStates[1] = 2; // amber
    if (phase === 'F4') groupStates[2] = groupStates[3] = 2; // amber

    events.push(
      { type: 'tx', delay: 120, generator: txGroupStatus, frameType: FrameType.INFORMATION, description: '0x87 - Estado grupos' },
      { type: 'rx', delay: 50, generator: () => rxGroupStatus(counter++, groupStates), frameType: FrameType.INFORMATION, description: '0x87 - Estados actualizados' }
    );
  }

  messageCounter = counter;
  return events;
}

// Generate phase change sequence
function generatePhaseChangeSequence(newPhase: string, newPosition: number): SimulationEvent[] {
  let counter = messageCounter;

  const events: SimulationEvent[] = [
    // State change notification
    { type: 'tx', delay: 0, generator: txStateChangeNotif, frameType: FrameType.INFORMATION, description: '0xC5 - Cambio de fase detectado' },
    { type: 'rx', delay: 60, generator: () => rxStateChange(counter++, 6, 'E'), frameType: FrameType.INFORMATION, description: `0xC5 - Nueva fase: ${newPhase}` },

    // Update cycle position
    { type: 'tx', delay: 100, generator: txCyclePosition, frameType: FrameType.INFORMATION, description: '0x86 - Verificar posición' },
    { type: 'rx', delay: 40, generator: () => rxCyclePosition(counter++, newPhase, newPosition, 700), frameType: FrameType.INFORMATION, description: `0x86 - Fase ${newPhase}, inicio` },

    // Update group status
    { type: 'tx', delay: 80, generator: txGroupStatus, frameType: FrameType.INFORMATION, description: '0x87 - Actualizar grupos' },
  ];

  const groupStates = newPhase === 'F1' ? [3, 3, 1, 1, 1, 1, 1, 1]
    : newPhase === 'F2' ? [2, 2, 1, 1, 1, 1, 1, 1]
    : newPhase === 'F3' ? [1, 1, 3, 3, 1, 1, 1, 1]
    : [1, 1, 2, 2, 1, 1, 1, 1];

  events.push({
    type: 'rx',
    delay: 50,
    generator: () => rxGroupStatus(counter++, groupStates),
    frameType: FrameType.INFORMATION,
    description: `0x87 - Grupos fase ${newPhase}`
  });

  messageCounter = counter;
  return events;
}

// Generate alarm event sequence
function generateAlarmSequence(alarmCode: string): SimulationEvent[] {
  let counter = messageCounter;

  const events: SimulationEvent[] = [
    // TML - indicates previous error
    { type: 'tx', delay: 0, generator: txTML, frameType: FrameType.TEST_COMMUNICATION, description: 'TML - Error detectado' },
    { type: 'rx', delay: 30, generator: rxTRT, frameType: FrameType.TEST_COMMUNICATION, description: 'TRT - Confirmación' },

    // Query alarms
    { type: 'tx', delay: 100, generator: txAlarmQuery, frameType: FrameType.INFORMATION, description: '0x83 - Consulta alarmas' },
    { type: 'rx', delay: 60, generator: () => rxAlarmQuery(counter++, [alarmCode]), frameType: FrameType.INFORMATION, description: `0x83 - Alarma ${alarmCode}: ${AlarmCodes[alarmCode as keyof typeof AlarmCodes]}` },

    // Regulator state (might have changed)
    { type: 'tx', delay: 150, generator: txRegulatorState, frameType: FrameType.INFORMATION, description: '0x82 - Verificar estado' },
    { type: 'rx', delay: 50, generator: () => rxRegulatorState(counter++, 6, 'E', 'F1'), frameType: FrameType.INFORMATION, description: '0x82 - Estado mantenido' },
  ];

  messageCounter = counter;
  return events;
}

export const useDetailedSimulation = create<DetailedSimulationState>((set, get) => ({
  isRunning: false,
  speed: 1,
  currentSequence: 'idle',
  sequenceIndex: 0,

  regulatorState: 6,
  currentPhase: 'F1',
  currentPlan: 2,
  cyclePosition: 0,
  cycleTime: 850, // Plan 2 = 85s
  phaseDurations: [360, 110, 230, 150], // Plan 2 default
  planVersion: 0,
  activeAlarms: [],
  activeDetectors: 0,
  pedestrianDemands: 0,

  intervalId: null,
  eventQueue: [],

  start: () => {
    const state = get();
    if (state.isRunning) return;

    const frameStore = useFrameStore.getState();
    frameStore.clearFrames();

    // Reset counter
    messageCounter = 1;

    // Start with startup sequence
    const startupEvents = generateStartupSequence();

    let eventIndex = 0;
    let lastEventTime = Date.now();
    let currentPhaseIndex = 0;
    const phases = ['F1', 'F2', 'F3', 'F4'];
    let phasePosition = 0;
    let cyclePosition = 0;
    let pollCounter = 0;
    let inStartup = true;
    let lastPlanVersion = state.planVersion;

    const processEvents = () => {
      const currentState = get();
      if (!currentState.isRunning) return;

      // Detect plan change from usePlanSchedule
      if (currentState.planVersion !== lastPlanVersion) {
        currentPhaseIndex = 0;
        phasePosition = 0;
        cyclePosition = 0;
        lastPlanVersion = currentState.planVersion;
        lastEventTime = Date.now();
        if (!inStartup) {
          set({ currentPhase: 'F1', cyclePosition: 0 });
        }
      }

      const now = Date.now();
      const frameStore = useFrameStore.getState();

      if (inStartup && eventIndex < startupEvents.length) {
        // Process startup sequence
        const event = startupEvents[eventIndex];
        const expectedTime = lastEventTime + (event.delay / currentState.speed);

        if (now >= expectedTime) {
          const frame = createFrame(event.frameType, event.generator(), event.type);
          frameStore.addFrame(frame);
          lastEventTime = now;
          eventIndex++;
        }
      } else {
        // Startup complete, switch to normal operation
        if (inStartup) {
          inStartup = false;
          lastEventTime = now;
          pollCounter = 0;
        }

        // Update cycle position (every 100ms = 1 decisecond at 1x)
        const elapsed = (now - lastEventTime) * currentState.speed / 100;

        if (elapsed >= 1) {
          phasePosition += Math.floor(elapsed);
          cyclePosition += Math.floor(elapsed);
          lastEventTime = now;

          // Check phase transition
          if (phasePosition >= currentState.phaseDurations[currentPhaseIndex]) {
            phasePosition = 0;
            currentPhaseIndex = (currentPhaseIndex + 1) % 4;

            if (currentPhaseIndex === 0) {
              cyclePosition = 0;
            }

            const newPhase = phases[currentPhaseIndex];
            set({ currentPhase: newPhase, cyclePosition });

            // Generate phase change sequence
            const changeEvents = generatePhaseChangeSequence(newPhase, cyclePosition);
            changeEvents.forEach((event, i) => {
              setTimeout(() => {
                if (get().isRunning) {
                  const frame = createFrame(event.frameType, event.generator(), event.type);
                  frameStore.addFrame(frame);
                }
              }, (event.delay + i * 20) / currentState.speed);
            });
          }

          set({ cyclePosition });

          // Periodic polling (every ~30 deciseconds = 3 seconds at 1x)
          pollCounter += Math.floor(elapsed);
          if (pollCounter >= 30) {
            pollCounter = 0;

            const pollEvents = generatePollingSequence(phases[currentPhaseIndex], cyclePosition);
            pollEvents.forEach((event, i) => {
              setTimeout(() => {
                if (get().isRunning) {
                  const frame = createFrame(event.frameType, event.generator(), event.type);
                  frameStore.addFrame(frame);
                }
              }, (event.delay + i * 10) / currentState.speed);
            });

            // Random alarm (5% chance)
            if (Math.random() < 0.05) {
              const alarmCodes = Object.keys(AlarmCodes);
              const randomAlarm = alarmCodes[Math.floor(Math.random() * alarmCodes.length)];

              setTimeout(() => {
                if (get().isRunning) {
                  const alarmEvents = generateAlarmSequence(randomAlarm);
                  alarmEvents.forEach((event, i) => {
                    setTimeout(() => {
                      if (get().isRunning) {
                        const frame = createFrame(event.frameType, event.generator(), event.type);
                        frameStore.addFrame(frame);
                      }
                    }, (event.delay + i * 10) / currentState.speed);
                  });
                }
              }, 500 / currentState.speed);
            }
          }
        }
      }
    };

    const intervalId = setInterval(processEvents, 50); // Check every 50ms

    set({
      isRunning: true,
      intervalId,
      currentSequence: 'startup',
      currentPhase: 'F1',
      cyclePosition: 0
    });
  },

  stop: () => {
    const state = get();
    if (state.intervalId) {
      clearInterval(state.intervalId);
    }
    set({ isRunning: false, intervalId: null, currentSequence: 'idle' });
  },

  reset: () => {
    const state = get();
    state.stop();
    messageCounter = 1;
    set({
      currentPhase: 'F1',
      cyclePosition: 0,
      activeAlarms: [],
      activeDetectors: 0,
      pedestrianDemands: 0,
      sequenceIndex: 0
    });
    useFrameStore.getState().clearFrames();
  },

  setSpeed: (speed: number) => {
    set({ speed });
  },

  setPlan: (plan: number, cycleTime: number, phaseDurations: number[]) => {
    set({
      currentPlan: plan,
      cycleTime,
      phaseDurations,
      planVersion: get().planVersion + 1,
    });
  },
}));
